import { useDisclosure } from '@chakra-ui/react';
import {
  IPayment,
  PaymentStatusEnum,
  TransactableSourceType,
} from '@monetize/types/app';
import { doPrintPaymentToPdf } from '../../../api/paymentsService';
import DataTableActions from '../../../components/Monetize/DataTable/MDataTableActions';
import { TransactionApplicationModal } from '../../../components/TransactionApplication/TransactionApplicationModal';
import { useACL } from '../../../services/acl/acl';
import { downloadAndSaveToPDF } from '../../../utils';
import { UpdatePaymentModal } from '../../Invoices/createPayment/UpdatePaymentModal';
import { RefundApplicationModal } from './RefundApplicationModal';
import { RefundModal } from './RefundModal';

interface PaymentActionsProps {
  accountId: string;
  payment: IPayment;
  refetch: () => void;
}

export const PaymentActions = ({
  accountId,
  payment,
  refetch,
}: PaymentActionsProps) => {
  const { canDo } = useACL();
  const refundModalState = useDisclosure();
  const updatePaymentModalState = useDisclosure();
  const applyEntityToInvoicesModalState = useDisclosure();
  const refundApplicationModalState = useDisclosure();

  const canApplyPayment = canDo([['billing', 'create']]);
  const hasBillingUpdatePermissions = canDo([['billing', 'update']]);

  const { paymentStatus } = payment;

  const unAppliedAmount = payment.unAppliedAmount ?? 0;
  const appliedAmount = payment.appliedAmount ?? 0;
  const canRefund = unAppliedAmount > 0 || appliedAmount > 0;

  const actions = [
    {
      title: appliedAmount ? 'Edit Payment Applications' : 'Apply Payment',
      enabled: canApplyPayment && paymentStatus !== PaymentStatusEnum.FAILED,
      action: () => {
        applyEntityToInvoicesModalState.onOpen();
      },
    },
    {
      title: 'Refund',
      enabled:
        hasBillingUpdatePermissions &&
        (paymentStatus === PaymentStatusEnum.SUCCESS ||
          paymentStatus === PaymentStatusEnum.SUCCEEDED ||
          paymentStatus === PaymentStatusEnum.PARTIAL_REFUND),
      isDisabled: !canRefund,
      tooltipMsg: canRefund ? undefined : 'No amount available to refund',
      action: () => {
        if (unAppliedAmount > 0) {
          refundModalState.onOpen();
        } else {
          refundApplicationModalState.onOpen();
        }
      },
    },
    {
      title: 'Print to PDF',
      enabled: true,
      action: () => downloadAndSaveToPDF(() => doPrintPaymentToPdf(payment.id)),
    },
  ];

  if (!accountId || !payment) {
    return null;
  }

  return (
    <>
      <DataTableActions actions={actions} />
      {refundModalState.open && (
        <RefundModal
          isOpen
          onClose={(reload?: boolean) => {
            reload && refetch();
            refundModalState.onClose();
          }}
          paymentId={payment.id}
        />
      )}
      {updatePaymentModalState.open && (
        <UpdatePaymentModal
          onClose={(reload?: boolean) => {
            reload && refetch();
            updatePaymentModalState.onClose();
          }}
          accountId={accountId}
          payment={payment}
        />
      )}
      {applyEntityToInvoicesModalState.open && (
        <TransactionApplicationModal
          modalTitle="Apply Payment to Invoice"
          accountId={accountId}
          sourceId={payment.id}
          sourceType={TransactableSourceType.payment}
          sourceOriginalAmount={payment.amount}
          currency={payment.currency}
          onClose={() => applyEntityToInvoicesModalState.onClose()}
        />
      )}
      {refundApplicationModalState.open && (
        <RefundApplicationModal
          accountId={accountId}
          sourceId={payment.id}
          sourceOriginalAmount={payment.amount}
          currency={payment.currency}
          onClose={() => refundApplicationModalState.onClose()}
          onContinue={() => refundModalState.onOpen()}
        />
      )}
    </>
  );
};
